import { useEffect, useId, useRef, useState } from "react";
import { notifyReadonlyBlocked, readonlyBlocksVoice } from "./readonly";
import { ScribeRealtimeSession } from "./scribe-realtime";
import {
  classifySendFailure,
  classifyStartFailure,
  voiceFailureMessageKey
} from "./voice-recording";

import { TRANSCRIBE_BITS_PER_SECOND, TRANSCRIBE_STOP_BYTES } from "../shared/transcribe-limits";
import { reportStaleCsrf } from "./identity-sync";

const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus"] as const;
const MAX_RECORDING_SECONDS = 40 * 60;
// Cost guard (Pavel): never pay Scribe for silence. Voice activity tracked
// from the analyser; 30 s without speech stops the recording, and a take with
// no speech at all is never sent for transcription. Fail-open when the
// analyser is unavailable (better to transcribe than to drop).
const VOICE_RMS = 0.025;
const SILENCE_STOP_MS = 30_000;

type TranscriptState = "pending" | "processing" | "done" | "error";
type VoicePhase = "idle" | "requesting_permission" | "recording" | "uploading" | "error";

export interface VoiceRecordingState {
  sourceId: string;
  recording: boolean;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }>;
}

function speechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as (new () => SpeechRecognitionLike) | null;
}

export interface VoiceableInputProps {
  scope: "q1_voice" | "q2_voice" | "profile_voice" | "feedback_audio" | "app_voice";
  value: string;
  attachmentId?: string;
  required?: boolean;
  maxRecordingSeconds?: number;
  csrfToken: string;
  mini?: boolean;
  // Accessible name for the textarea; screen readers otherwise announce only
  // the placeholder. Callers pass the question/field text where they have it.
  ariaLabel?: string;
  t(key: string): string;
  onTextChange(value: string): void;
  onAttachmentReady(input: { attachmentId: string; transcriptStatus: TranscriptState; transcript?: string }): void;
  onAttachmentClear?(): void;
  onRecordingStateChange?(state: VoiceRecordingState): void;
}

// Hybrid mic: the batch MediaRecorder always runs for the optional archive,
// while Scribe Realtime is the primary live transcript. Browser
// SpeechRecognition is the live fallback. A final batch request is reserved
// for the case where neither live path produced text. No playback UI.
export function VoiceableInput(props: VoiceableInputProps) {
  const [phase, setPhase] = useState<VoicePhase>("idle");
  const [elapsed, setElapsed] = useState(0);
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recordingSourceId = useId();
  const recordingStateCallbackRef = useRef(props.onRecordingStateChange);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordedBytesRef = useRef(0);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const cancelledRef = useRef(false);
  const elapsedRef = useRef(0);
  const levelRef = useRef(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const ringRef = useRef<HTMLCanvasElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const rafRef = useRef(0);
  // Cursor split captured at record start: live text streams between them.
  const insertRef = useRef<{ prefix: string; suffix: string }>({ prefix: "", suffix: "" });
  const recogRef = useRef<SpeechRecognitionLike | null>(null);
  const scribeRef = useRef<ScribeRealtimeSession | null>(null);
  const wantLiveRef = useRef(false);
  const liveFinalRef = useRef("");
  const lastVoiceRef = useRef(0);
  const voicedRef = useRef(false);
  const maxSeconds = Math.min(props.maxRecordingSeconds ?? MAX_RECORDING_SECONDS, MAX_RECORDING_SECONDS);
  recordingStateCallbackRef.current = props.onRecordingStateChange;

  useEffect(() => {
    const recording = phase === "recording";
    recordingStateCallbackRef.current?.({ sourceId: recordingSourceId, recording });
    return () => {
      if (recording) recordingStateCallbackRef.current?.({ sourceId: recordingSourceId, recording: false });
    };
  }, [phase, recordingSourceId]);

  useEffect(() => () => {
    stopLive();
    void scribeRef.current?.close();
    scribeRef.current = null;
    teardown();
  }, []);

  useEffect(() => {
    if (phase !== "recording") return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [phase]);

  useEffect(() => {
    if (phase !== "recording") return;
    const timer = window.setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
      if (elapsedRef.current === 35 * 60) setWarning(props.t("voice.warning35"));
      if (elapsedRef.current === 39 * 60) setWarning(props.t("voice.warning39"));
      if (elapsedRef.current >= maxSeconds) stopRecording(false);
      trackVoiceActivity();
    }, 1000);
    return () => window.clearInterval(timer);
  }, [maxSeconds, phase, props.t]);

  useEffect(() => {
    let active = true;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const animate = (time: number) => {
      if (!active) return;
      const analyser = phase === "recording" ? analyserRef.current : null;
      let bins: Uint8Array | null = null;
      let target = 0.14;
      if (analyser) {
        const data = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(data);
        bins = data;
        target = Math.min(1, data.reduce((sum, value) => sum + value, 0) / Math.max(1, data.length) / 120);
      }
      levelRef.current += (target - levelRef.current) * 0.18;
      if (ringRef.current) drawOrbRing(ringRef.current, { bins, tick: reduceMotion ? 0 : time, level: levelRef.current, live: phase === "recording" });
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      active = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [phase]);

  // Compose the field value around the captured cursor point and keep the
  // caret at the end of the inserted text.
  function setInsertedText(text: string) {
    const { prefix, suffix } = insertRef.current;
    const trimmed = text.trim();
    const before = prefix && trimmed ? (prefix.replace(/\s+$/, "") + (prefix.endsWith("\n") ? "\n" : " ")) : prefix;
    const after = suffix && trimmed && !/^[\s.,;:!?]/.test(suffix) ? ` ${suffix}` : suffix;
    const composed = trimmed ? `${before}${trimmed}${after}` : `${prefix}${suffix}`;
    props.onTextChange(composed);
    const caret = (trimmed ? before.length + trimmed.length : prefix.length);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el && document.activeElement === el) el.setSelectionRange(caret, caret);
    });
  }

  function currentRms(): number | null {
    const analyser = analyserRef.current;
    if (!analyser) return null;
    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (const value of data) {
      const x = (value - 128) / 128;
      sum += x * x;
    }
    return Math.sqrt(sum / data.length);
  }

  function trackVoiceActivity() {
    const rms = currentRms();
    if (rms === null) {
      // Analyser unavailable: fail-open, treat as voiced.
      voicedRef.current = true;
      lastVoiceRef.current = Date.now();
      return;
    }
    if (rms > VOICE_RMS) {
      voicedRef.current = true;
      lastVoiceRef.current = Date.now();
    } else if (Date.now() - lastVoiceRef.current > SILENCE_STOP_MS) {
      const hadVoice = voicedRef.current;
      setWarning(props.t("voice.silenceStopped"));
      stopRecording(!hadVoice);
    }
  }

  function startLive(preserveTranscript = false) {
    const Ctor = speechRecognitionCtor();
    if (!Ctor) return;
    stopLive();
    wantLiveRef.current = true;
    if (!preserveTranscript) liveFinalRef.current = "";
    const recog = new Ctor();
    recogRef.current = recog;
    // The app writes the resolved interface language onto <html lang>, so this
    // reads a real answer in practice. F119: an empty attribute (the recorder
    // mounted before that effect ran) is us not knowing what the speaker is
    // about to say, and dictating English into a Czech recognizer produces
    // gibberish just as surely as the other way round - so not knowing listens
    // in English. Czech is kept for a reader we KNOW is Czech.
    const lang = (document.documentElement.lang || "en").startsWith("cs") ? "cs-CZ" : "en-US";
    recog.lang = lang;
    recog.continuous = true;
    recog.interimResults = true;
    recog.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const piece = event.results[i][0].transcript;
        if (event.results[i].isFinal) liveFinalRef.current += (liveFinalRef.current ? " " : "") + piece.trim();
        else interim += piece;
      }
      setInsertedText([liveFinalRef.current, interim].filter(Boolean).join(" "));
    };
    recog.onerror = (event) => {
      const code = event?.error ?? "";
      // Live preview died (Brave/Arc network error etc.): batch recorder keeps
      // running, Scribe delivers the transcript at stop.
      if (code && code !== "no-speech" && code !== "aborted") wantLiveRef.current = false;
    };
    recog.onend = () => {
      if (!wantLiveRef.current || !recogRef.current) return;
      try {
        recogRef.current.start();
      } catch {
        wantLiveRef.current = false;
      }
    };
    try {
      recog.start();
    } catch {
      wantLiveRef.current = false;
    }
  }

  function stopLive() {
    wantLiveRef.current = false;
    const recog = recogRef.current;
    recogRef.current = null;
    if (recog) {
      try {
        recog.stop();
      } catch { /* already stopped */ }
    }
  }

  async function startRecording() {
    // Impersonation preview: refuse BEFORE the partner invests a recording
    // the server would reject on submit anyway.
    if (readonlyBlocksVoice()) {
      notifyReadonlyBlocked();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError(props.t("voice.unsupported"));
      return;
    }
    setPhase("requesting_permission");
    setError(null);
    setWarning(null);
    const el = textareaRef.current;
    const caret = el && document.activeElement === el ? (el.selectionStart ?? props.value.length) : props.value.length;
    insertRef.current = { prefix: props.value.slice(0, caret), suffix: props.value.slice(caret) };
    liveFinalRef.current = "";
    voicedRef.current = false;
    lastVoiceRef.current = Date.now();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MIME_CANDIDATES.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? "";
      // 🔴 Speech bitrate, stated rather than inherited. A browser that
      // defaults high (Safari records audio/mp4 AAC) would otherwise put a
      // long take past TRANSCRIBE_MAX_BYTES, and the route can only refuse it.
      const recorder = new MediaRecorder(stream, {
        ...(mime ? { mimeType: mime } : {}),
        audioBitsPerSecond: TRANSCRIBE_BITS_PER_SECOND
      });
      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      cancelledRef.current = false;
      elapsedRef.current = 0;
      recordedBytesRef.current = 0;
      setElapsed(0);
      setupAnalyser(stream);
      recorder.ondataavailable = (event) => {
        if (event.data.size === 0) return;
        chunksRef.current.push(event.data);
        // 🔴 A take that grew past what one transcription request may carry is
        // stopped and KEPT, never allowed to become a refusal the reader can
        // do nothing about. The bitrate above makes this unreachable on any
        // ordinary browser; this is the floor under it.
        recordedBytesRef.current += event.data.size;
        if (recordedBytesRef.current >= TRANSCRIBE_STOP_BYTES && recorder.state === "recording") {
          setWarning(props.t("voice.warning39"));
          stopRecording(false);
        }
      };
      recorder.onstop = () => {
        void (async () => {
        const chunks = chunksRef.current;
        const cancelled = cancelledRef.current;
        const type = recorder.mimeType || mime || chunks[0]?.type || "audio/webm";
        const realtime = scribeRef.current;
        scribeRef.current = null;
        if (realtime) {
          const realtimeText = await realtime.stop().catch(() => "");
          if (realtimeText.trim()) liveFinalRef.current = realtimeText.trim();
        }
        stopLive();
        teardown();
        if (cancelled || chunks.length === 0) {
          setInsertedText(liveFinalRef.current.trim() && !cancelled ? liveFinalRef.current : "");
          setPhase("idle");
          return;
        }
        void finish(new Blob(chunks, { type }), type);
        })();
      };
      recorder.start(1000);
      setPhase("recording");
      let realtimeFailed = false;
      const realtime = new ScribeRealtimeSession({
        onTranscript: (text) => {
          liveFinalRef.current = text;
          setInsertedText(text);
        },
        onFailure: () => {
          realtimeFailed = true;
          if (scribeRef.current === realtime) {
            scribeRef.current = null;
          }
          void realtime.close();
          if (recorderRef.current?.state === "recording") startLive(true);
        }
      });
      try {
        await realtime.start(stream, props.csrfToken);
        if (recorder.state === "inactive" || realtimeFailed) {
          await realtime.close();
        } else {
          scribeRef.current = realtime;
        }
      } catch {
        await realtime.close();
        startLive();
      }
    } catch (error) {
      stopLive();
      teardown();
      setPhase("error");
      // F98: "voice.permissionError" was the answer to every cause, including
      // a busy device and an unsupported container.
      setError(props.t(voiceFailureMessageKey(classifyStartFailure(error))));
    }
  }

  function stopRecording(cancelled: boolean) {
    cancelledRef.current = cancelled;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
  }

  async function finish(blob: Blob, mime: string) {
    const liveText = liveFinalRef.current.trim();
    if (liveText) {
      // The live preview already wrote the answer; drop any interim tail and
      // archive the audio with that transcript (no second Scribe payment).
      setInsertedText(liveText);
      setPhase("idle");
      void archive(blob, mime, liveText);
      return;
    }
    // Live preview dead or empty: remove interim text, then Scribe (only for
    // takes that actually contained speech).
    setInsertedText("");
    if (!voicedRef.current || blob.size <= 800) {
      setPhase("idle");
      return;
    }
    setPhase("uploading");
    try {
      const text = await transcribeLive(blob, mime, props.csrfToken);
      if (!text) throw new Error("transcribe_failed");
      setInsertedText(text);
      setPhase("idle");
      void archive(blob, mime, text);
    } catch (error) {
      setPhase("error");
      setError(props.t(voiceFailureMessageKey(classifySendFailure(error, navigator.onLine !== false))));
    }
  }

  // Archive the raw take (voice-clone material) together with the transcript
  // we already have: the Worker stores it as done and skips the paid queue.
  async function archive(blob: Blob, mime: string, transcript: string) {
    const form = new FormData();
    const extension = mime.includes("mp4") ? "m4a" : mime.includes("ogg") ? "ogg" : "webm";
    form.set("scope", props.scope);
    form.set("transcript", transcript);
    form.set("file", new File([blob], `voice.${extension}`, { type: mime }));
    try {
      const response = await fetch("/api/attachments", {
        method: "POST",
        credentials: "include",
        headers: { "X-Launchpad-CSRF": props.csrfToken },
        body: form
      });
      const body = await response.json() as { attachmentId?: string; transcriptStatus?: TranscriptState };
      if (!response.ok || !body.attachmentId) return;
      props.onAttachmentReady({ attachmentId: body.attachmentId, transcriptStatus: body.transcriptStatus ?? "done", transcript });
    } catch { /* archive is optional until R2 is on */ }
  }

  function setupAnalyser(stream: MediaStream) {
    try {
      const context = new AudioContext();
      const source = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      audioContextRef.current = context;
      analyserRef.current = analyser;
    } catch {
      analyserRef.current = null;
    }
  }

  function teardown() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    void audioContextRef.current?.close().catch(() => undefined);
    cancelAnimationFrame(rafRef.current);
    streamRef.current = null;
    recorderRef.current = null;
    analyserRef.current = null;
    audioContextRef.current = null;
    chunksRef.current = [];
  }

  const recording = phase === "recording";
  const busy = phase === "requesting_permission" || phase === "uploading";
  // One layout everywhere: the text field with a mic at its side (Pavel:
  // "mikrofon po strane a textove pole", no mode toggles, no playback UI).
  return (
    <div className={`voice-input voice-inline${props.mini ? "" : " voice-inline-main"}`}>
      <div className="voice-inline-row">
        {/* Mic FIRST, on the left: speaking is the primary way to answer,
            typing the fallback (Pavel). Controls live under the icon. */}
        <div className="voice-mic-col">
          <button
            type="button"
            className={`voice-inline-mic ${recording ? "recording" : ""}`}
            aria-label={recording ? props.t("voice.finish") : props.t("voice.start")}
            title={recording ? props.t("voice.finish") : props.t("voice.start")}
            data-voice-start={!recording && !busy ? true : undefined}
            data-voice-stop={recording ? true : undefined}
            aria-busy={busy}
            disabled={busy}
            onClick={() => {
              if (recording) stopRecording(false);
              else if (["idle", "error"].includes(phase)) void startRecording();
            }}
          >
            <canvas ref={ringRef} className="voice-orb-ring" aria-hidden="true" />
            <svg className="voice-orb-mic" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path fill="currentColor" d="M12 14.5a3.3 3.3 0 0 0 3.3-3.3V6.3a3.3 3.3 0 1 0-6.6 0v4.9a3.3 3.3 0 0 0 3.3 3.3Zm5.8-3.3a5.8 5.8 0 0 1-11.6 0H4.4a7.6 7.6 0 0 0 6.7 7.5v2.5h1.8v-2.5a7.6 7.6 0 0 0 6.7-7.5h-1.8Z" />
            </svg>
          </button>
        </div>
        <textarea
          ref={textareaRef}
          value={props.value}
          onChange={(event) => props.onTextChange(event.target.value)}
          rows={props.mini ? 3 : 6}
          required={props.required}
          readOnly={readonlyBlocksVoice()}
          aria-label={props.ariaLabel ?? props.t("voice.textFieldLabel")}
          placeholder={props.t("voice.textPlaceholder")}
        />
        {recording && (
          <div className="voice-recording-panel" aria-live="polite">
            <span className="voice-recording-live">
              <span className="voice-recording-dot" aria-hidden="true" />
              <span>
                <strong>{props.t("voice.status.recording")} {formatDuration(elapsed)}</strong>
                <small>{props.t("voice.liveTranscript")}</small>
              </span>
            </span>
            <span className="voice-recording-actions">
              <button className="primary-button" data-voice-stop type="button" onClick={() => stopRecording(false)}>
                {props.t("voice.stopAndSend")}
              </button>
              <button className="quiet-link" type="button" onClick={() => stopRecording(true)}>
                {props.t("voice.cancel")}
              </button>
            </span>
          </div>
        )}
      </div>
      {phase === "requesting_permission" && <p className="voice-status" role="status">{props.t("voice.requesting")}</p>}
      {phase === "uploading" && <p className="voice-status">{props.t("voice.transcribing")}</p>}
      {warning && <p className="voice-warning">{warning}</p>}
      {error && <p className="voice-error" role="alert">{error}</p>}
    </div>
  );
}

function formatDuration(totalSeconds: number): string {
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

// Shared: send a fresh recording through the Worker to Scribe and get the
// text back immediately.
export async function transcribeLive(
  blob: Blob,
  mime: string,
  csrfToken: string,
  options?: { onUploadComplete?: () => void }
): Promise<string> {
  const form = new FormData();
  const extension = mime.includes("mp4") ? "m4a" : mime.includes("ogg") ? "ogg" : "webm";
  form.set("file", new File([blob], `live-note.${extension}`, { type: mime }));
  if (options?.onUploadComplete) {
    return await new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", "/api/transcribe-live");
      xhr.withCredentials = true;
      xhr.setRequestHeader("X-Launchpad-CSRF", csrfToken);
      xhr.upload.addEventListener("load", options.onUploadComplete!, { once: true });
      xhr.addEventListener("load", () => {
        let body: { text?: string; error?: string } | null = null;
        try {
          body = JSON.parse(xhr.responseText) as { text?: string; error?: string };
        } catch {
          body = null;
        }
        if (xhr.status < 200 || xhr.status >= 300 || !body || typeof body.text !== "string") {
          // 422 = the audio carried no speech. That is a fact about the
          // recording, not a failure to retry, so it must not reach the
          // caller as "transcribe_failed" (which offers a paid resend).
          // 413 and 429 are facts too, and each has its own way out.
          // 🔴 F161: XHR bypasses the fetch observer in identity-sync, so a
          // stale-token refusal has to report itself from here, or the tab
          // answers every take with "transcript failed, send again" forever
          // (Martina Prenosilova, prod 2026-09-09: 46 minutes of retries).
          const marker = sendFailureMarker(xhr.status, body?.error);
          if (marker === "csrf_stale") reportStaleCsrf();
          reject(new Error(marker));
          return;
        }
        resolve(body.text.trim());
      }, { once: true });
      xhr.addEventListener("error", () => reject(new Error("transcribe_failed")), { once: true });
      xhr.addEventListener("abort", () => reject(new Error("transcribe_failed")), { once: true });
      xhr.send(form);
    });
  }
  const response = await fetch("/api/transcribe-live", {
    method: "POST",
    credentials: "include",
    headers: { "X-Launchpad-CSRF": csrfToken },
    body: form
  });
  // An EMPTY transcript is a valid result (silence, cleaned pause tokens) -
  // callers decide what silence means. Throw only on transport/API failure.
  const body = await response.json().catch(() => null) as { text?: string; error?: string } | null;
  // (The fetch path is seen by the identity-sync observer, which already
  // triggers the token repair; only the reason needs naming here.)
  if (!response.ok || !body || typeof body.text !== "string") throw new Error(sendFailureMarker(response.status, body?.error));
  return body.text.trim();
}

/**
 * What the transcription route said, as a marker `classifySendFailure` knows.
 * Anything unnamed stays `transcribe_failed`, which is the only one of these
 * that a plain resend can clear.
 */
function sendFailureMarker(status: number, errorCode?: string | null): string {
  if (status === 422) return "no_speech";
  if (status === 413) return "recording_too_large";
  if (status === 429) return "rate_limited";
  // The page's token belongs to a session the server has since renewed. The
  // recording is fine; only the token must change, and the app is taking a
  // fresh one the moment this is reported.
  if (status === 403 && errorCode === "csrf_invalid") return "csrf_stale";
  return "transcribe_failed";
}

// Sci-fi listening ring: radial spectrum rays around the orb core. With live
// audio the rays follow the voice; idle they breathe slowly, so the orb feels
// like an intelligence that is actually listening (Pavel's brief).
export function drawOrbRing(
  canvas: HTMLCanvasElement,
  input: { bins: Uint8Array | null; tick: number; level: number; live: boolean }
): void {
  const size = canvas.clientWidth;
  if (!size) return;
  const dpr = window.devicePixelRatio || 1;
  if (canvas.width !== size * dpr) {
    canvas.width = size * dpr;
    canvas.height = size * dpr;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, size, size);
  const cx = size / 2;
  const cy = size / 2;
  const coreRadius = size * 0.3;
  const rays = 72;
  const maxLen = size * 0.16;
  for (let i = 0; i < rays; i += 1) {
    const angle = (i / rays) * Math.PI * 2 - Math.PI / 2;
    let value: number;
    if (input.bins && input.bins.length > 0) {
      const bin = input.bins[Math.floor((i / rays) * Math.min(input.bins.length, 96))] / 255;
      value = Math.min(1, bin * 1.35);
    } else {
      value = 0.1 + 0.07 * Math.sin(input.tick * 0.0016 + i * 0.42) + 0.04 * Math.sin(input.tick * 0.0009 + i * 0.11);
    }
    const start = coreRadius + 5;
    const length = 2 + value * maxLen * (input.live ? 1 : 0.55);
    const alpha = (input.live ? 0.3 : 0.18) + value * 0.65;
    const hue = 196 + value * 24;
    ctx.strokeStyle = `hsla(${hue}, 92%, ${58 + value * 16}%, ${alpha})`;
    ctx.lineWidth = 1.6;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(angle) * start, cy + Math.sin(angle) * start);
    ctx.lineTo(cx + Math.cos(angle) * (start + length), cy + Math.sin(angle) * (start + length));
    ctx.stroke();
  }
}
